import { useEffect, useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import type { CheckedState } from '@radix-ui/react-checkbox'
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatDateTime } from '@/lib/locale'
import { useAuditLogs, useDeleteAuditLogs } from '@/features/platformAdmin/queries'
import type { AuditLogRow } from '@/features/platformAdmin/types'

const PAGE_SIZE = 50

const fmtDate = (v: string | null) => {
  if (!v) return ''
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? v : formatDateTime(d)
}

const actorLabel = (r: AuditLogRow) =>
  r.actor_email ||
  r.actor_name ||
  (r.actor_type === 'system' ? 'system' : r.actor_id ? String(r.actor_id) : '-')

const tenantLabel = (r: AuditLogRow) =>
  r.tenant_name || (r.tenant_id ? r.tenant_id.slice(0, 8) : '-')

const actionLabel = (a: string) => a.replace(/_/g, ' ').replace(/\./g, ' › ')

const errMsg = (err: unknown, fallback = 'Failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function AuditTab() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const canDelete = hasPermission('admin.audit.delete')

  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirming, setConfirming] = useState(false)

  const auditQ = useAuditLogs({
    action: '',
    resource_type: '',
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })
  const deleteM = useDeleteAuditLogs()

  // Drop selections from rows that aren't on the current page so a Delete
  // never targets a row the user can't see.
  const rows = auditQ.data?.logs ?? []
  const total = auditQ.data?.total ?? rows.length
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(rows.map((r) => String(r.id)))
      const next = new Set<string>()
      for (const id of prev) if (visible.has(id)) next.add(id)
      return next
    })
  }, [rows])

  // Clamp page if total shrinks below current offset (e.g. after a delete).
  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(Math.max(0, pageCount - 1))
  }, [page, pageCount])

  const visibleIds = useMemo(
    () => rows.map((r) => String(r.id)).filter((id) => id !== 'undefined'),
    [rows],
  )
  const allSelectedState: CheckedState =
    visibleIds.length === 0
      ? false
      : visibleIds.every((id) => selected.has(id))
        ? true
        : visibleIds.some((id) => selected.has(id))
          ? 'indeterminate'
          : false

  const toggleAll = (next: CheckedState) => {
    if (next === true) {
      setSelected((prev) => {
        const out = new Set(prev)
        for (const id of visibleIds) out.add(id)
        return out
      })
    } else {
      setSelected((prev) => {
        const out = new Set(prev)
        for (const id of visibleIds) out.delete(id)
        return out
      })
    }
  }

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const onDelete = async () => {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    try {
      const r = await deleteM.mutateAsync(ids)
      toast({
        title: `Deleted ${r.deleted} log${r.deleted === 1 ? '' : 's'}.`,
      })
      setSelected(new Set())
      setConfirming(false)
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const columns: ColumnDef<AuditLogRow>[] = [
    ...(canDelete
      ? [{
          id: '__select',
          header: () => (
            <Checkbox
              checked={allSelectedState}
              onCheckedChange={toggleAll}
              disabled={visibleIds.length === 0}
              aria-label="Select all visible"
            />
          ),
          cell: ({ row }: { row: { original: AuditLogRow } }) => {
            const id = String(row.original.id)
            return (
              <Checkbox
                checked={selected.has(id)}
                onCheckedChange={() => toggleOne(id)}
                aria-label={`Select log ${id}`}
              />
            )
          },
        }]
      : []),
    {
      accessorKey: 'created_at',
      header: 'When',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{fmtDate(row.original.created_at)}</span>
      ),
    },
    {
      id: 'actor',
      header: 'Actor',
      cell: ({ row }) => <span className="text-xs">{actorLabel(row.original)}</span>,
    },
    {
      accessorKey: 'action',
      header: 'Action',
      cell: ({ row }) => (
        <Badge variant="outline" className="font-mono text-[10px]">
          {actionLabel(row.original.action)}
        </Badge>
      ),
    },
    {
      id: 'resource',
      header: 'Resource',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">
          {row.original.resource_type ?? '-'}
          {row.original.resource_id ? ` / ${String(row.original.resource_id).slice(0, 12)}` : ''}
        </span>
      ),
    },
    {
      id: 'tenant',
      header: 'Tenant',
      cell: ({ row }) => <span className="text-xs">{tenantLabel(row.original)}</span>,
    },
  ]

  const fromIdx = total === 0 ? 0 : page * PAGE_SIZE + 1
  const toIdx = Math.min((page + 1) * PAGE_SIZE, total)

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Workspace-level events only - workspace creations, plan changes, suspensions,
        deletions, owner-account removals, staff-role changes, managed payouts, and
        invoice receipts paid through the Bokapsys-managed rail. Tenant-internal
        activity is visible inside each tenant&apos;s own Audit tab.
      </p>

      <Toolbar>
        <span className="text-xs text-muted-foreground">
          {total === 0
            ? 'No logs yet.'
            : `Showing ${fromIdx}–${toIdx} of ${total}`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {canDelete && selected.size > 0 ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setConfirming(true)}
              disabled={deleteM.isPending}
            >
              <Trash2 className="size-4" />
              Delete {selected.size} selected
            </Button>
          ) : null}
        </div>
      </Toolbar>

      <Card>
        <CardContent className="p-3">
          {auditQ.isLoading && rows.length === 0 ? (
            <LoadingSpinner />
          ) : auditQ.isError ? (
            <Alert variant="destructive">
              <AlertDescription>Failed to load audit logs.</AlertDescription>
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState title="No audit activity yet" />
          ) : (
            <DataTable columns={columns} rows={rows} />
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          Page {page + 1} of {pageCount}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || auditQ.isFetching}
          >
            <ChevronLeft className="size-4" />
            Prev
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={page + 1 >= pageCount || auditQ.isFetching}
          >
            Next
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      <Modal.Confirm
        open={confirming}
        onOpenChange={(v) => !v && setConfirming(false)}
        title="Delete audit logs"
        body={`Permanently delete ${selected.size} audit log${selected.size === 1 ? '' : 's'}? Audit deletions are themselves audited (who/when), but the deleted rows are gone for good.`}
        confirmLabel={`Delete ${selected.size}`}
        confirmVariant="danger"
        onConfirm={onDelete}
        busy={deleteM.isPending}
      />
    </div>
  )
}
